/**
 * Guided recovery confirmation (#804): Reset to maintenance and Reinstall,
 * each stating plainly what it erases.
 *
 * - Reset to maintenance → `POST /machines/:id/talos-reset` (Admin). A Talos
 *   reset over the management tunnel that wipes STATE (config, node identity)
 *   and EPHEMERAL (/var: container images, kubelet and etcd data, and the
 *   Longhorn replicas stored there) and reboots into maintenance mode. It is
 *   not graceful: the box isn't drained and doesn't leave etcd first. On the
 *   site's only control plane that erases the cluster itself, so the dialog
 *   says so and asks for the hostname.
 * - Reinstall → `POST /machines/:id/reinstall` (Engineer). For a box in
 *   maintenance mode: forget its recorded hardware, rediscover, push a fresh
 *   config; the install writes Talos to the system drive. The server re-probes
 *   maintenance mode itself and refuses (409) a box that is running its
 *   config, so this is offered regardless of the `maintenanceDetected` flag
 *   (a 5 s probe often never sets it, #788).
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RotateCcw, X } from 'lucide-react';
import { machinesApi } from '@/api/client';
import {
  controlPlaneImpact,
  recoveryErrorMessage,
  RESET_LIVE_WINDOW_MS,
  type RecoveryAction,
} from '@/lib/boxRecovery';
import { formatAge, APID_STALE_MS } from '@/lib/boxes';
import { siteReadinessQueryKey } from '@/lib/readiness';
import type { ClusterNode, Machine } from '@/types';

interface Props {
  action: RecoveryAction;
  machine: Machine;
  /** The box's node, if it has one (for the "not drained first" warning). */
  node: ClusterNode | null;
  /** Every machine on the site, to judge control-plane impact. */
  siteMachines: Machine[];
  onClose: () => void;
}

function Callout({ tone, children, testId }: { tone: 'danger' | 'warn' | 'info'; children: ReactNode; testId?: string }) {
  const cls = {
    danger: 'bg-status-error/10 border-status-error/40 text-status-error',
    warn: 'bg-status-warning/10 border-status-warning/30 text-status-warning',
    info: 'bg-surface-deep border-surface-elevated text-silver-muted',
  }[tone];
  return (
    <div className={`flex gap-2 border rounded-lg px-3 py-2 text-xs ${cls}`} data-testid={testId}>
      {tone !== 'info' && <AlertTriangle size={14} className="shrink-0 mt-0.5" />}
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export default function BoxRecoveryDialog({ action, machine, node, siteMachines, onClose }: Props) {
  const queryClient = useQueryClient();
  const [confirmName, setConfirmName] = useState('');
  const now = Date.now();
  const isReset = action === 'reset';

  const mutation = useMutation({
    mutationFn: () => (isReset ? machinesApi.talosReset(machine.id) : machinesApi.reinstall(machine.id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      if (machine.siteId) {
        queryClient.invalidateQueries({ queryKey: ['cluster-nodes', machine.siteId] });
        queryClient.invalidateQueries({ queryKey: siteReadinessQueryKey(machine.siteId) });
      }
      onClose();
    },
  });
  const err = mutation.isError ? recoveryErrorMessage(mutation.error, action) : null;

  const cp = controlPlaneImpact(machine, siteMachines);
  const lastSeenAge = machine.siderolinkLastSeen ? now - new Date(machine.siderolinkLastSeen).getTime() : null;
  const tunnelStale = lastSeenAge === null || Number.isNaN(lastSeenAge) || lastSeenAge > RESET_LIVE_WINDOW_MS;
  const apidAge = machine.apidCheckedAt ? now - new Date(machine.apidCheckedAt).getTime() : null;
  const runningItsConfig = machine.apidState === 'serving' && apidAge !== null && apidAge <= APID_STALE_MS && !machine.maintenanceDetected;

  // Typing the hostname is required for the wipe, and for nothing else.
  const needsName = isReset;
  const canConfirm = !mutation.isPending && (!needsName || confirmName.trim() === machine.hostname);
  const title = isReset ? 'Reset to maintenance' : 'Reinstall';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title}: ${machine.hostname}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isReset ? 'bg-status-error/20' : 'bg-amber-500/20'}`}>
              <RotateCcw size={18} className={isReset ? 'text-status-error' : 'text-amber-400'} />
            </div>
            <div>
              <h3 className="text-silver-bright font-semibold">{title}</h3>
              <p className="text-xs text-silver-muted font-mono">{machine.hostname}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-silver-muted hover:text-silver-bright" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 text-sm text-silver-muted">
          {isReset ? (
            <>
              <p>
                Jeket sends the box a reset over its management tunnel. The box wipes two areas of its
                system drive, restarts in maintenance mode, reconnects, and is set up again from its
                certified configuration.
              </p>
              <div>
                <p className="text-xs font-semibold text-silver-bright mb-1">Erased on this box</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Its configuration and cluster identity.</li>
                  <li>
                    All of its working data: container images, Kubernetes
                    {cp.isControlPlane ? ' and cluster-database (etcd)' : ''} data, and{' '}
                    <strong className="text-silver-bright">the site storage (Longhorn) replicas kept on this box</strong>.
                    A volume whose only healthy copy is on this box loses its data.
                  </li>
                </ul>
                <p className="text-xs mt-1">Kept: the Talos installation itself, and everything on other boxes.</p>
              </div>
              {node && (
                <Callout tone="warn">
                  <p>Workloads on this box stop at once: the reset doesn&apos;t drain the box first. Drain it beforehand if you can.</p>
                </Callout>
              )}
              {cp.onlyControlPlane && (
                <Callout tone="danger" testId="only-control-plane-warning">
                  <p className="font-semibold">This is the site&apos;s only control-plane box.</p>
                  <p>
                    Resetting it erases the cluster itself. Every workload on the site, including JBS, stops,
                    and the cluster&apos;s state is lost for good: the site has to be set up again.
                    Contact Jeket support before you do this on a working site.
                  </p>
                </Callout>
              )}
              {!cp.onlyControlPlane && cp.losesQuorum && (
                <Callout tone="danger" testId="quorum-loss-warning">
                  <p className="font-semibold">The cluster stops.</p>
                  <p>
                    This is one of {cp.members} control-plane boxes and only {cp.healthyAfter} healthy one
                    {cp.healthyAfter === 1 ? '' : 's'} would remain. Kubernetes needs {cp.quorum} to keep running,
                    so every workload on the site, including JBS, stops and may not recover on its own.
                    Contact Jeket support first.
                  </p>
                </Callout>
              )}
              {cp.isMember && !cp.onlyControlPlane && !cp.losesQuorum && (
                <Callout tone="warn" testId="control-plane-warning">
                  <p>
                    This is a control-plane box. The cluster keeps running on the other {cp.healthyAfter}, but
                    this box leaves the cluster database without warning. Contact Jeket support before
                    resetting a control-plane box on a working site.
                  </p>
                </Callout>
              )}
              {tunnelStale && (
                <Callout tone="warn" testId="tunnel-stale-warning">
                  <p>
                    {lastSeenAge === null || Number.isNaN(lastSeenAge)
                      ? 'The box has never connected over its management tunnel.'
                      : `The box was last in contact ${formatAge(lastSeenAge)} ago.`}{' '}
                    A reset needs it online (in contact within the last minute), so it will probably be refused.
                    If the box can&apos;t be reached, start it from the site install image instead.
                  </p>
                </Callout>
              )}
              <div className="space-y-1.5">
                <label htmlFor="confirm-hostname" className="text-xs text-silver-muted">
                  Type <span className="font-mono text-silver-bright">{machine.hostname}</span> to confirm
                </label>
                <input
                  id="confirm-hostname"
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={machine.hostname}
                  autoComplete="off"
                  className="w-full bg-surface-deep border border-status-error/30 rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder:text-silver-muted/30 focus:border-status-error/60 focus:outline-none"
                />
              </div>
            </>
          ) : (
            <>
              <p>
                Use this when the box is in maintenance mode — after Reset to maintenance, or after starting it
                from the site install image — and hasn&apos;t continued setting up by itself.
              </p>
              <p>
                Jeket forgets the hardware it recorded for this box, reads it again, and sends the box its
                configuration. The box installs Talos onto its system drive and rejoins the site.
              </p>
              <div>
                <p className="text-xs font-semibold text-silver-bright mb-1">Erased on this box</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Everything on its system drive, which the install overwrites.</li>
                </ul>
              </div>
              {machine.maintenanceDetected ? (
                <Callout tone="info"><p>The box was last seen in maintenance mode.</p></Callout>
              ) : runningItsConfig ? (
                <Callout tone="warn" testId="not-in-maintenance-warning">
                  <p>
                    This box is running its installed configuration (its Talos API answers with this site&apos;s
                    credentials), so it isn&apos;t in maintenance mode and the reinstall will be refused.
                    Reset it to maintenance first.
                  </p>
                </Callout>
              ) : (
                <Callout tone="info">
                  <p>Jeket checks that the box is in maintenance mode before reinstalling, and refuses if it isn&apos;t.</p>
                </Callout>
              )}
            </>
          )}

          {err && (
            <div className="text-xs text-status-error bg-status-error/10 border border-status-error/30 rounded px-3 py-2 space-y-1" data-testid="recovery-error">
              <p>{err.message}</p>
              {err.hint && <p className="text-silver-muted">{err.hint}</p>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canConfirm}
            data-testid="confirm-recovery"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isReset ? 'bg-status-error text-white hover:bg-status-error/80' : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
            }`}
          >
            {mutation.isPending ? 'Working…' : isReset ? 'Reset to maintenance' : 'Reinstall'}
          </button>
        </div>
      </div>
    </div>
  );
}
